/**
 * The project architecture as a picture: the project's layers as bands, top
 * to bottom; in each band one group per repository, holding the areas of that
 * repository the layer claims; a last band for what no layer claims yet. The
 * connections between repositories are drawn over it as labelled lines.
 *
 * The browser lays the bands out (groups and cards wrap, so a narrow pane
 * needs no sideways scroll); everything a line must keep clear of is then
 * measured from the DOM — every card, each piece of a group's heading — and
 * the lines are routed through what is left by projectArchEdges.ts, which
 * also says where each label goes and how much room each gap between two
 * bands needs. They are drawn above the bands and under the cards, in the
 * colours every canvas here uses (flowStyle.ts): muted, and the accent for
 * what belongs to the selection.
 *
 * A repository's own area-to-area links are the business of its own map.
 * Here they show, dashed, only for the area under the pointer or selected,
 * so the connections between repositories stay the story.
 */
import './projectArchitecture.css';

import { ArrowRightArrowLeft, Box, Database, FileCode, Folder, Globe, Link, Pencil, Plus } from '@gravity-ui/icons';
import { Button, Dropdown } from '@heroui/react';
import { type DragEvent, type MouseEvent, type ReactNode, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FLOW_ACCENT, FLOW_MUTED } from '../flowStyle';
import type { ArtTone } from '../ui/Art.js';
import { FitText } from '../ui/FitText.js';
import { Hint } from '../ui/Hint.js';
import { edgeGrid, type EdgeRect, type EdgeRequest, type EdgeScene, type EdgeSize, marginHeight, routeEdges, stripHeight } from './projectArchEdges.js';
import { connectionCaption, connectionsOf, emptyRepositoryText, endCaption, type ProjectArchArea, type ProjectArchEnd, type ProjectArchGroup, type ProjectArchModel, type ProjectConnectionKind, splitGroupAreas } from './projectArchModel.js';
import type { ArchSelection } from './ProjectArchPanel.js';

/** One tone per layer, in order, as the repository's own map gives its layers. */
const LAYER_TONES: readonly ArtTone[] = ['blue', 'teal', 'violet', 'amber', 'green', 'rose', 'slate'];
/** The band for what no layer claims. */
const UNPLACED = '~';
/** The clear strip between two bands (the canvas's gap, 2.5rem), and the height of one track in it: a line and its label. */
const STRIP = 40;
const LANE = 24;
/** The margin round the picture (the canvas's padding, 1rem). */
const MARGIN = 16;
/** A label before it has been measured, and one with no room for its words (the kind's icon alone). */
const LABEL: EdgeSize = { width: 64, height: 22 };
const COMPACT_LABEL: EdgeSize = { width: 20, height: 20 };
/** A repository's own links are drawn this far off the lanes the connections take, so the two never coincide. */
const INSIDE_BIAS = 5;

const KIND_ICONS: Record<ProjectConnectionKind, ReactNode> = {
  http: <Globe />,
  package: <Box />,
  protocol: <FileCode />,
  data: <Database />,
  other: <Link />,
};

interface Band {
  id: string;
  /** A layer of the document, as opposed to "Not placed yet". */
  layer: boolean;
  title: string;
  description?: string;
  tone: ArtTone;
  groups: ProjectArchGroup[];
}

interface Geometry {
  signature: string;
  /** Where every group and card ended up. */
  anchors: Map<string, EdgeRect>;
  /** The picture as the router sees it: its size, the bands, the groups, and what a line keeps clear of. */
  scene: EdgeScene;
  /** Each connection's label at its full size, by the connection's index. */
  labels: Map<number, EdgeSize>;
}

const NOTHING_MEASURED: Geometry = { signature: '', anchors: new Map(), scene: { width: 0, height: 0, bands: [], groups: [], obstacles: [] }, labels: new Map() };

type Dragged = { kind: 'area'; key: string; from: string } | { kind: 'group'; rootId: string; from: string };

const groupAnchor = (bandId: string, rootId: string) => `group|${bandId}|${rootId}`;
const areaAnchor = (key: string) => `area|${key}`;

export interface ProjectArchPictureProps {
  model: ProjectArchModel;
  editing: boolean;
  selection: ArchSelection;
  onSelect(next: ArchSelection): void;
  /** Put one area in a layer; `undefined` sends it back to wherever its repository is. */
  onMoveArea(key: string, layerId: string | undefined): void;
  /** Move what one band shows of a repository to another layer; `undefined` is "Not placed yet". */
  onMoveGroup(rootId: string, from: string | undefined, to: string | undefined): void;
  onAddLayer(): void;
}

export function ProjectArchPicture({ model, editing, selection, onSelect, onMoveArea, onMoveGroup, onAddLayer }: ProjectArchPictureProps) {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [geometry, setGeometry] = useState<Geometry>(NOTHING_MEASURED);
  const [hover, setHover] = useState<string | null>(null);
  const [hoverConnection, setHoverConnection] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [dropBand, setDropBand] = useState<string | null>(null);
  const dragged = useRef<Dragged | null>(null);
  const markerId = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const bands: Band[] = useMemo(() => {
    const out: Band[] = model.layers.map((l, i) => ({ id: l.id, layer: true, title: l.title, description: l.description, tone: LAYER_TONES[i % LAYER_TONES.length], groups: l.repositories }));
    if (model.unplaced.length) out.push({ id: UNPLACED, layer: false, title: 'Not placed yet', description: editing ? 'No layer holds these yet. Move a repository, or one area, into a layer.' : 'No layer of the project holds these yet.', tone: 'slate', groups: model.unplaced });
    return out;
  }, [model, editing]);

  const selectedArea = selection?.kind === 'area' ? selection.key : undefined;
  const selectedConnection = selection?.kind === 'connection' ? selection.index : undefined;
  const selectedRepository = selection?.kind === 'repository' ? selection.rootId : undefined;

  /** The connections that belong to what is selected (or under the pointer), and the areas and repositories at their ends. */
  const lit = useMemo(() => {
    const connections = new Set<number>();
    if (selectedConnection !== undefined) connections.add(selectedConnection);
    const area = selectedArea ? model.areas.get(selectedArea) : undefined;
    if (area) for (const c of connectionsOf(model, { rootId: area.rootId, areaKey: area.key })) connections.add(c.index);
    if (selectedRepository) for (const c of connectionsOf(model, { rootId: selectedRepository })) connections.add(c.index);
    const areas = new Set<string>();
    const repositories = new Set<string>();
    if (area) areas.add(area.key);
    if (selectedRepository) repositories.add(selectedRepository);
    for (const index of connections) {
      for (const end of [model.connections[index]?.from, model.connections[index]?.to]) {
        if (!end?.known) continue;
        if (end.areaKey) areas.add(end.areaKey);
        else repositories.add(end.rootId);
      }
    }
    if (area) for (const e of model.internal) if (e.fromKey === area.key || e.toKey === area.key) areas.add(e.fromKey === area.key ? e.toKey : e.fromKey);
    return { connections, areas, repositories, any: selectedConnection !== undefined || !!area || !!selectedRepository };
  }, [model, selectedArea, selectedConnection, selectedRepository]);

  /** Support areas something points at are drawn without being asked for. */
  const alwaysShown = useMemo(() => {
    const keys = new Set<string>();
    for (const c of model.connections) for (const end of [c.from, c.to]) if (end.areaKey) keys.add(end.areaKey);
    if (selectedArea) keys.add(selectedArea);
    return keys;
  }, [model, selectedArea]);

  // ── Measuring: where every group and card ended up, what a line must keep clear of, and how big each label is ──
  const measure = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const origin = canvas.getBoundingClientRect();
    const rectOf = (el: Element): EdgeRect => {
      const r = el.getBoundingClientRect();
      return { x: Math.round(r.left - origin.left), y: Math.round(r.top - origin.top), width: Math.round(r.width), height: Math.round(r.height) };
    };
    const text = (r: EdgeRect) => `${r.x},${r.y},${r.width},${r.height}`;
    const anchors = new Map<string, EdgeRect>();
    canvas.querySelectorAll<HTMLElement>('[data-anchor]').forEach((el) => anchors.set(el.dataset.anchor ?? '', rectOf(el)));
    const bands = [...canvas.querySelectorAll('[data-band]')].map(rectOf);
    const groups = [...anchors].filter(([key]) => key.startsWith('group|')).map(([, rect]) => rect);
    // Every card, and each piece of a heading (a name, a count, a button): a line passes none of them.
    const obstacles = [...[...anchors].filter(([key]) => key.startsWith('area|')).map(([, rect]) => rect), ...[...canvas.querySelectorAll('[data-parch-obstacle]')].map(rectOf)];
    const labels = new Map<number, EdgeSize>();
    canvas.querySelectorAll<HTMLElement>('[data-parch-sizer]').forEach((el) => labels.set(Number(el.dataset.parchSizer), { width: Math.ceil(el.offsetWidth), height: Math.ceil(el.offsetHeight) }));
    const scene: EdgeScene = { width: Math.round(origin.width), height: Math.round(origin.height), bands, groups, obstacles };
    const signature = [`${scene.width}x${scene.height}`, bands.map(text).join(' '), obstacles.map(text).join(' '), [...anchors].map(([k, r]) => `${k}:${text(r)}`).join(' '), [...labels].map(([k, l]) => `${k}:${l.width}x${l.height}`).join(' ')].join(';');
    setGeometry((prev) => (prev.signature === signature ? prev : { signature, anchors, scene, labels }));
  }, []);
  // After every render: the picture is laid out by the browser, so anything that changed it may have moved a card.
  useLayoutEffect(measure);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    // A label measured before its typeface arrived is the wrong width.
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [measure]);

  // ── The lines: routed again only when the picture has moved or the connections have changed, never for a selection ──
  const homeOf = useMemo(() => new Map(model.repositories.map((r) => [r.rootId, r.homeLayer ?? UNPLACED])), [model]);
  const grid = useMemo(() => edgeGrid(geometry.scene), [geometry]);
  const connections = useMemo(() => {
    const anchorOf = (end: ProjectArchEnd): string | undefined => {
      if (!end.known) return undefined;
      if (end.areaKey && geometry.anchors.has(areaAnchor(end.areaKey))) return areaAnchor(end.areaKey);
      // A whole repository: its group's box, in the layer most of it is in.
      const home = groupAnchor(homeOf.get(end.rootId) ?? UNPLACED, end.rootId);
      if (geometry.anchors.has(home)) return home;
      return bands.map((b) => groupAnchor(b.id, end.rootId)).find((a) => geometry.anchors.has(a));
    };
    const requests: EdgeRequest[] = [];
    for (const c of model.connections) {
      const fromId = anchorOf(c.from);
      const toId = anchorOf(c.to);
      if (!fromId || !toId || fromId === toId) continue;
      requests.push({ id: String(c.index), fromId, toId, from: geometry.anchors.get(fromId)!, to: geometry.anchors.get(toId)!, label: geometry.labels.get(c.index) ?? LABEL });
    }
    // A track each, a label high, in the strip a connection turns in: its label sits on its own line.
    return routeEdges(requests, grid, { laneStep: LANE, compact: COMPACT_LABEL });
  }, [model, geometry, grid, bands, homeOf]);
  /** A repository's own links, for the area under the pointer or selected: over the same lanes, a little off them, and taking no room. */
  const inside = useMemo(() => {
    const active = hover ?? selectedArea;
    const requests: EdgeRequest[] = [];
    if (active) {
      for (const e of model.internal) {
        if (e.fromKey !== active && e.toKey !== active) continue;
        const from = geometry.anchors.get(areaAnchor(e.fromKey));
        const to = geometry.anchors.get(areaAnchor(e.toKey));
        if (from && to) requests.push({ id: `${e.fromKey}>${e.toKey}`, fromId: areaAnchor(e.fromKey), toId: areaAnchor(e.toKey), from, to });
      }
    }
    return requests.length ? [...routeEdges(requests, grid, { bias: INSIDE_BIAS }).routes.values()] : [];
  }, [model, geometry, grid, hover, selectedArea]);
  /** How many tracks each gap holds — above the first band, between each two, below the last: a gap grows to hold its own. */
  const loads = connections.loads;

  // ── Drag and drop (the "Move to…" menus do the same from the keyboard) ──
  const canDrop = (band: Band) => {
    const d = dragged.current;
    if (!d || d.from === band.id) return false;
    return d.kind === 'group' || band.layer;
  };
  const bandDragOver = (band: Band) => (e: DragEvent) => {
    if (!canDrop(band)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropBand !== band.id) setDropBand(band.id);
  };
  const bandDrop = (band: Band) => (e: DragEvent) => {
    const d = dragged.current;
    dragged.current = null;
    setDropBand(null);
    if (!d || d.from === band.id) return;
    e.preventDefault();
    if (d.kind === 'area') {
      if (band.layer) onMoveArea(d.key, band.id);
    } else onMoveGroup(d.rootId, d.from === UNPLACED ? undefined : d.from, band.layer ? band.id : undefined);
  };
  const startDrag = (what: Dragged) => (e: DragEvent) => {
    e.stopPropagation();
    dragged.current = what;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', what.kind === 'area' ? what.key : what.rootId);
  };
  const endDrag = () => {
    dragged.current = null;
    setDropBand(null);
  };

  /** What a band leaves under it beyond the canvas's own gap: the tracks of the strip below it, or of the margin below the last band. */
  const bandRoom = (at: number) => {
    if (at < bands.length - 1) return stripHeight(loads[at + 1] ?? 0, LANE, STRIP) - STRIP;
    const below = loads[bands.length] ?? 0;
    return below ? Math.max(0, marginHeight(below, LANE, MARGIN) - (editing ? STRIP : MARGIN)) : 0;
  };

  /** A click on the ground, not on anything in the picture, lets go of the selection. */
  const clearOnGround = (e: MouseEvent) => {
    if (!(e.target as HTMLElement).closest('button, a, input, [role="menuitem"], .parch-edge__hit')) onSelect(null);
  };

  const moveMenu = (label: string, current: string, items: Array<{ id: string; text: string }>, onAction: (id: string) => void) => (
    <Dropdown.Popover placement="bottom end">
      <Dropdown.Menu aria-label={label} disabledKeys={[current]} onAction={(key) => onAction(String(key))}>
        {items.map((item) => (
          <Dropdown.Item key={item.id} id={item.id} textValue={item.text}>
            {item.text}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown.Popover>
  );
  const layerItems = model.layers.map((l) => ({ id: l.id, text: l.title }));

  const areaCard = (area: ProjectArchArea, band: Band) => {
    const dimmed = lit.any && !lit.areas.has(area.key) && !lit.repositories.has(area.rootId);
    return (
      <div key={area.key} className="parch-area-wrap" data-anchor={areaAnchor(area.key)} data-editing={editing || undefined}>
        <button
          type="button"
          className="flow-card parch-area"
          data-selected={selectedArea === area.key ? '' : undefined}
          data-dimmed={dimmed ? '' : undefined}
          data-quiet={area.support ? '' : undefined}
          data-parch-area={area.key}
          aria-pressed={selectedArea === area.key}
          draggable={editing}
          onDragStart={editing ? startDrag({ kind: 'area', key: area.key, from: band.id }) : undefined}
          onDragEnd={endDrag}
          onClick={() => onSelect({ kind: 'area', key: area.key })}
          onMouseEnter={() => setHover(area.key)}
          onMouseLeave={() => setHover((h) => (h === area.key ? null : h))}
          onFocus={() => setHover(area.key)}
          onBlur={() => setHover((h) => (h === area.key ? null : h))}
        >
          <FitText className="flow-card__title">
            {area.title}
          </FitText>
          <span className="flow-card__meta">
            <FitText className="min-w-0 flex-1 tabular-nums">{area.files !== undefined ? `${area.files.toLocaleString()} ${area.files === 1 ? 'file' : 'files'}` : area.path}</FitText>
          </span>
        </button>
        {editing ? (
          <Hint text="Move to another layer">
            <Dropdown>
              <Button size="sm" variant="ghost" isIconOnly className="parch-area__move" aria-label={`Move ${area.title} to another layer`} isDisabled={!layerItems.length}>
                <ArrowRightArrowLeft className="size-3" />
              </Button>
              {moveMenu(`Move ${area.title} to`, area.layerId ?? '', [...layerItems, ...(area.placedByHand ? [{ id: UNPLACED, text: 'Back with its repository' }] : [])], (id) => onMoveArea(area.key, id === UNPLACED ? undefined : id))}
            </Dropdown>
          </Hint>
        ) : null}
      </div>
    );
  };

  const group = (g: ProjectArchGroup, band: Band) => {
    const key = `${band.id}|${g.rootId}`;
    const { shown, hidden } = splitGroupAreas(g.areas, alwaysShown, expanded.has(key));
    const repo = model.repositories.find((r) => r.rootId === g.rootId);
    return (
      <div key={g.rootId} className="parch-group" data-anchor={groupAnchor(band.id, g.rootId)} data-selected={selectedRepository === g.rootId ? '' : undefined} data-lit={lit.repositories.has(g.rootId) ? '' : undefined} data-parch-group={g.rootId}>
        <div className="parch-group__head" draggable={editing} onDragStart={editing ? startDrag({ kind: 'group', rootId: g.rootId, from: band.id }) : undefined} onDragEnd={endDrag}>
          <button type="button" className="parch-group__name" data-parch-obstacle="" aria-pressed={selectedRepository === g.rootId} onClick={() => onSelect({ kind: 'repository', rootId: g.rootId })}>
            <Folder className="size-3.5 shrink-0" aria-hidden="true" />
            <FitText>{g.name}</FitText>
          </button>
          {g.areas.length ? <span className="parch-group__count" data-parch-obstacle="">{g.areas.length === 1 ? '1 area' : `${g.areas.length} areas`}</span> : null}
          {editing ? (
            <Dropdown>
              <Button size="sm" variant="ghost" className="parch-group__move" data-parch-obstacle="" isDisabled={!layerItems.length} aria-label={`Move ${g.name} to another layer`}>
                Move to…
              </Button>
              {moveMenu(`Move ${g.name} to`, band.id, [...layerItems, ...(band.layer ? [{ id: UNPLACED, text: 'Out of every layer' }] : [])], (id) => onMoveGroup(g.rootId, band.layer ? band.id : undefined, id === UNPLACED ? undefined : id))}
            </Dropdown>
          ) : null}
        </div>
        {g.areas.length ? (
          <div className="parch-areas">
            {shown.map((a) => areaCard(a, band))}
            {hidden || expanded.has(key) ? (
              <button
                type="button"
                className="parch-more"
                data-parch-obstacle=""
                title="Docs, configuration, assets and tests"
                aria-expanded={expanded.has(key)}
                onClick={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (!next.delete(key)) next.add(key);
                    return next;
                  })
                }
              >
                {expanded.has(key) ? 'Code only' : `and ${hidden} more`}
              </button>
            ) : null}
          </div>
        ) : (
          <p className="parch-group__empty" data-parch-obstacle="">{emptyRepositoryText(repo)}</p>
        )}
      </div>
    );
  };

  return (
    <div className="parch-stage flow-frame" onClick={clearOnGround} data-parch-stage>
      <div className="parch-canvas" ref={canvasRef} style={loads[0] ? { paddingTop: marginHeight(loads[0], LANE, MARGIN) } : undefined}>
        <svg className="parch-edges" aria-hidden="true">
          <defs>
            {(['muted', 'accent'] as const).map((tone) => (
              <marker key={tone} id={`${markerId}-${tone}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" markerUnits="userSpaceOnUse" orient="auto">
                <path d="M 0 1 L 10 5 L 0 9 z" style={{ fill: tone === 'accent' ? FLOW_ACCENT : FLOW_MUTED }} />
              </marker>
            ))}
          </defs>
          {inside.map((r) => (
            <path key={r.id} d={r.d} className="parch-edge parch-edge--inside" style={{ stroke: FLOW_MUTED }} markerEnd={`url(#${markerId}-muted)`} />
          ))}
          {model.connections.map((c) => {
            const route = connections.routes.get(String(c.index));
            if (!route) return null;
            const mine = lit.connections.has(c.index) || hoverConnection === c.index;
            return (
              <g key={c.index} data-parch-connection={c.index}>
                <path d={route.d} className="parch-edge" style={{ stroke: mine ? FLOW_ACCENT : FLOW_MUTED, strokeWidth: mine ? 2 : 1.5, opacity: mine ? 1 : lit.any ? 0.18 : 0.75 }} markerEnd={`url(#${markerId}-${mine ? 'accent' : 'muted'})`} />
                <path d={route.d} className="parch-edge__hit" onClick={() => onSelect({ kind: 'connection', index: c.index })} onMouseEnter={() => setHoverConnection(c.index)} onMouseLeave={() => setHoverConnection(null)} />
              </g>
            );
          })}
        </svg>

        {bands.map((band, at) => (
          <section key={band.id} className="parch-band" style={{ marginBottom: bandRoom(at) || undefined }} data-band={band.id} data-tone={band.tone} data-quiet={band.layer ? undefined : ''} data-drop={dropBand === band.id ? '' : undefined} onDragOver={editing ? bandDragOver(band) : undefined} onDragLeave={editing ? () => setDropBand((b) => (b === band.id ? null : b)) : undefined} onDrop={editing ? bandDrop(band) : undefined}>
            <div className="card-art flow-band parch-band__ground" data-tone={band.tone} data-motif="arc" aria-hidden="true" />
            <header className="parch-band__head">
              <span className="flow-band__dot" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                {band.layer ? (
                  <button type="button" className="parch-band__title" aria-pressed={selection?.kind === 'layer' && selection.id === band.id} onClick={() => onSelect({ kind: 'layer', id: band.id })} data-parch-layer={band.id}>
                    <FitText>{band.title}</FitText>
                    {editing ? <Pencil className="size-3 shrink-0 opacity-60" aria-label="Edit" /> : null}
                  </button>
                ) : (
                  <h2 className="parch-band__title">{band.title}</h2>
                )}
                {band.description ? (
                  <p className="parch-band__blurb" title={band.description}>
                    {band.description}
                  </p>
                ) : null}
              </div>
            </header>
            {band.groups.length ? <div className="parch-groups">{band.groups.map((g) => group(g, band))}</div> : <p className="parch-band__empty" data-parch-obstacle="">{editing ? 'Nothing here yet. Drag a repository or an area in, or use its “Move to…”.' : 'Nothing in this layer yet.'}</p>}
          </section>
        ))}

        {editing ? (
          <button type="button" className="parch-add-layer" onClick={onAddLayer} data-parch-add-layer>
            <Plus className="size-3.5" /> Add a layer
          </button>
        ) : null}

        <div className="parch-labels">
          {model.connections.map((c) => {
            const label = connections.routes.get(String(c.index))?.label;
            if (!label) return null;
            const mine = lit.connections.has(c.index) || hoverConnection === c.index;
            // Where the words do not fit (two things side by side with little between them): the kind's icon alone, so the line and its arrow still show.
            const compact = label.compact;
            return (
              <button
                key={c.index}
                type="button"
                className="parch-edge-label"
                data-compact={compact ? '' : undefined}
                style={{ left: label.x, top: label.y }}
                data-selected={selectedConnection === c.index ? '' : undefined}
                data-lit={mine ? '' : undefined}
                data-dimmed={lit.any && !mine ? '' : undefined}
                aria-pressed={selectedConnection === c.index}
                aria-label={`${connectionCaption(c)}: from ${endCaption(c.from)} to ${endCaption(c.to)}`}
                title={`${compact ? `${connectionCaption(c)}: ` : ''}${endCaption(c.from)} → ${endCaption(c.to)}`}
                onClick={() => onSelect({ kind: 'connection', index: c.index })}
                onMouseEnter={() => setHoverConnection(c.index)}
                onMouseLeave={() => setHoverConnection(null)}
                onFocus={() => setHoverConnection(c.index)}
                onBlur={() => setHoverConnection(null)}
                data-parch-label={c.index}
              >
                {KIND_ICONS[c.kind]}
                {compact ? null : <span className="truncate">{connectionCaption(c)}</span>}
              </button>
            );
          })}
          {/* Every label at its full size, unseen: what the lines are routed for, whatever size a label is then shown at. */}
          {model.connections.map((c) => (
            <span key={c.index} className="parch-edge-label parch-edge-label--sizer" aria-hidden="true" data-parch-sizer={c.index}>
              {KIND_ICONS[c.kind]}
              <FitText>{connectionCaption(c)}</FitText>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
